/**
 * SourceChip — Clickable reference badge that opens chunk detail.
 *
 * Shared component for ALL reference displays across Kuutio:
 * - Chat messages
 * - AIScorePanel
 * - Solution analysis
 * - Node modal
 * - Report
 *
 * Click → opens ChunkDetailModal with content + enrichment data.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { BookOpen, X, ExternalLink, Tag, HelpCircle, Loader2 } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import { useTranslation } from '../hooks/useTranslation';

// ── Styled Components ──────────────────────────────────

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 6px;
  border: 1px solid rgba(147, 51, 234, 0.2);
  background: rgba(147, 51, 234, 0.08);
  color: #c4a0f0;
  font-size: 12px;
  font-weight: 500;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: all 0.15s;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    ${p => p.$clickable && `
      background: rgba(147, 51, 234, 0.15);
      border-color: rgba(147, 51, 234, 0.4);
      color: #d4b5f7;
    `}
  }

  svg { flex-shrink: 0; opacity: 0.6; }
`;

const Marker = styled.span`
  font-weight: 700;
  font-size: 11px;
  color: ${p => p.$analysis ? '#888' : '#c084fc'};
  flex-shrink: 0;
`;

const RelevanceBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
  flex-shrink: 0;
`;

// ── Modal Styles ──────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: #1e1e32;
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: 16px;
  width: 90%;
  max-width: 640px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.6);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  padding-right: 12px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  flex-shrink: 0;
  &:hover { color: #fff; background: rgba(255, 255, 255, 0.1); }
`;

const ModalBody = styled.div`
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const MetaBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 5px;
  background: ${p => p.$color || 'rgba(255, 255, 255, 0.06)'};
  color: ${p => p.$textColor || '#aaa'};
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg { width: 11px; height: 11px; }
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #888;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;

  svg { width: 12px; height: 12px; opacity: 0.5; }
`;

const ContentBlock = styled.div`
  font-size: 13px;
  color: #d4d4d4;
  line-height: 1.7;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 14px 16px;

  p { margin: 0 0 8px 0; &:last-child { margin-bottom: 0; } }
`;

const EnrichmentBlock = styled.div`
  padding: 12px 14px;
  background: rgba(147, 51, 234, 0.06);
  border: 1px solid rgba(147, 51, 234, 0.15);
  border-radius: 10px;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

const TagChip = styled.span`
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 5px;
  background: rgba(147, 51, 234, 0.12);
  color: #c084fc;
`;

const QuestionItem = styled.div`
  font-size: 12px;
  color: #aaa;
  padding: 2px 0;
  line-height: 1.4;
`;

const LoadingBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px;
  color: #888;
  font-size: 13px;

  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const DocLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(147, 51, 234, 0.1);
  border: 1px solid rgba(147, 51, 234, 0.2);
  color: #c084fc;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  align-self: flex-start;

  &:hover { background: rgba(147, 51, 234, 0.2); }
  svg { width: 14px; height: 14px; }
`;

// ── ChunkDetailModal ──────────────────────────────────

function ChunkDetailModal({ source, onClose }) {
  const { t } = useTranslation();
  const [chunkData, setChunkData] = useState(null);
  const [allChunks, setAllChunks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [docTitle, setDocTitle] = useState(null);
  const [showFullDoc, setShowFullDoc] = useState(true);
  const targetRef = React.useRef(null);

  // Fetch full document + find target chunk
  React.useEffect(() => {
    if (!source?.chunkId && !source?.documentId) return;

    setLoading(true);
    const fetchData = async () => {
      try {
        if (source.documentId) {
          const res = await fetch(API_ENDPOINTS.RAG_DOCUMENT(source.documentId));
          if (res.ok) {
            const data = await res.json();
            setDocTitle(data.document?.title);
            setAllChunks(data.chunks || []);
            const target = data.chunks?.find(c => c.id === source.chunkId);
            if (target) setChunkData(target);
          }
        }
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchData();
  }, [source?.chunkId, source?.documentId]);

  // Scroll to target chunk when doc loads
  React.useEffect(() => {
    if (showFullDoc && targetRef.current) {
      setTimeout(() => targetRef.current?.scrollIntoView({ behavior: 'instant', block: 'center' }), 50);
    }
  }, [showFullDoc, allChunks.length]);

  const displayTitle = cleanCitation(source.citation || source.title || t('common.unknownSource'));
  const enrichment = chunkData?.metadata;

  // Portal to document.body so modal escapes drawer overflow
  return createPortal(
    <Overlay onClick={onClose}>
      <ModalBox onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{displayTitle}</ModalTitle>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </ModalHeader>

        <ModalBody>
          {/* Meta badges */}
          <MetaRow>
            {source.marker && <MetaBadge $color="rgba(147, 51, 234, 0.15)" $textColor="#c084fc">{source.marker}</MetaBadge>}
            {source.relevance > 0 && <MetaBadge $color="rgba(34, 197, 94, 0.12)" $textColor="#4ade80">{source.relevance}% osuvuus</MetaBadge>}
            {source.section_heading && <MetaBadge>{source.section_heading}</MetaBadge>}
            {source.page_number && <MetaBadge>s. {source.page_number}</MetaBadge>}
            {docTitle && docTitle !== displayTitle && <MetaBadge><BookOpen size={11} /> {docTitle.substring(0, 40)}</MetaBadge>}
          </MetaRow>

          {loading ? (
            <LoadingBox><Loader2 size={16} /> {t('common.loadingContent')}</LoadingBox>
          ) : (
            <>
              {/* AI enrichment for target chunk */}
              {enrichment?.enriched && (
                <EnrichmentBlock>
                  <SectionLabel style={{ color: '#c084fc' }}>AI-rikastus</SectionLabel>

                  {enrichment.summary && (
                    <div style={{ fontSize: 13, color: '#d4d4d4', marginBottom: 8, lineHeight: 1.5 }}>
                      {enrichment.summary}
                    </div>
                  )}

                  {enrichment.tags?.length > 0 && (
                    <TagList style={{ marginBottom: enrichment.questions?.length ? 8 : 0 }}>
                      {enrichment.tags.map(t => <TagChip key={t}>{t}</TagChip>)}
                    </TagList>
                  )}

                  {enrichment.questions?.length > 0 && (
                    <div>
                      <SectionLabel><HelpCircle /> Kysymykset joihin tämä vastaa</SectionLabel>
                      {enrichment.questions.map((q, i) => (
                        <QuestionItem key={i}>• {q}</QuestionItem>
                      ))}
                    </div>
                  )}
                </EnrichmentBlock>
              )}

              {/* Full document — all chunks, target highlighted */}
              <div>
                <SectionLabel><BookOpen /> Dokumentti ({allChunks.length} osaa)</SectionLabel>
                {allChunks.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {allChunks.map((chunk) => {
                      const isTarget = chunk.id === source.chunkId;
                      return (
                        <ContentBlock
                          key={chunk.id}
                          ref={isTarget ? targetRef : null}
                          style={isTarget ? {
                            borderColor: 'rgba(147, 51, 234, 0.5)',
                            background: 'rgba(147, 51, 234, 0.08)',
                            boxShadow: '0 0 0 1px rgba(147, 51, 234, 0.3)',
                          } : { background: 'rgba(255, 255, 255, 0.015)', borderColor: 'rgba(255, 255, 255, 0.04)' }}
                        >
                          {isTarget && (
                            <div style={{ fontSize: 10, color: '#c084fc', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                              Viitattu kohta
                            </div>
                          )}
                          {chunk.section_heading && chunk.section_heading !== 'Unknown' && (
                            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{chunk.section_heading}{chunk.page_number ? ` · s. ${chunk.page_number}` : ''}</div>
                          )}
                          {chunk.content.split(/\n\n+/).map((para, i) => <p key={i}>{para.trim()}</p>)}
                        </ContentBlock>
                      );
                    })}
                  </div>
                ) : (
                  <ContentBlock>
                    {(chunkData?.content || source.excerpt || 'Sisältöä ei saatavilla.')
                      .split(/\n\n+/)
                      .map((para, i) => <p key={i}>{para.trim()}</p>)}
                  </ContentBlock>
                )}
              </div>

            </>
          )}
        </ModalBody>
      </ModalBox>
    </Overlay>,
    document.body
  );
}

// ── Helpers ──────────────────────────────────

function cleanCitation(raw) {
  return raw
    .replace(/\.[a-z]{2,4}\s*\|/gi, ' |')
    .split('|')[0].trim()
    .replace(/_/g, ' ');
}

// ── SourceChip Component ──────────────────────────────────

/**
 * SourceChip — clickable reference badge.
 *
 * @param {object} source - { citation, title, marker, chunkId, documentId, relevance, excerpt, type, section_heading, page_number }
 * @param {boolean} showRelevance - show relevance % badge
 * @param {'chip'|'inline'|'tag'} variant - visual style
 */
export default function SourceChip({ source, showRelevance = false, variant = 'chip' }) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const clickable = !!(source.chunkId || source.documentId);
  const displayTitle = cleanCitation(source.citation || source.title || t('common.unknownSource'));
  const isAnalysis = source.type === 'analysis';

  return (
    <>
      <Chip
        $clickable={clickable}
        onClick={() => clickable && setModalOpen(true)}
        title={clickable ? t('common.openSource') : displayTitle}
      >
        {source.marker && <Marker $analysis={isAnalysis}>{isAnalysis ? '·' : source.marker}</Marker>}
        {displayTitle}
        {showRelevance && source.relevance > 0 && <RelevanceBadge>{source.relevance}%</RelevanceBadge>}
        {clickable && <BookOpen size={11} />}
      </Chip>

      {modalOpen && (
        <ChunkDetailModal
          source={source}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

/**
 * SourceList — renders a list of SourceChips.
 * The citation list for AI output.
 */
const MoreSourcesLink = styled.button`
  background: none;
  border: none;
  padding: 2px 6px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  &:hover { color: rgba(255, 255, 255, 0.85); }
`;

export function SourceList({ sources = [], showRelevance = false, max, compact = false }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const capped = max && !expanded && sources.length > max;
  const visible = capped ? sources.slice(0, max) : sources;

  if (visible.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: compact ? 'row' : 'column', flexWrap: compact ? 'wrap' : 'nowrap', gap: compact ? 4 : 6 }}>
      {visible.map((source, i) => (
        <SourceChip
          key={source.chunkId || source.documentId || i}
          source={{ ...source, marker: source.marker || `[${i + 1}]` }}
          showRelevance={showRelevance}
        />
      ))}
      {max && sources.length > max && (
        <MoreSourcesLink type="button" onClick={() => setExpanded((v) => !v)}>
          {capped ? t('common.showMoreSources', { count: sources.length - max }) : t('common.showFewerSources')}
        </MoreSourcesLink>
      )}
    </div>
  );
}

export { ChunkDetailModal };
